import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { IconButton } from '@/components/common/IconButton';
import { Bell, Check, MailOpen, Inbox, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffNotifications } from '@/contexts/StaffNotificationsContext';

/**
 * NotificationsPage — full inbox with optimistic mark-as-read.
 *
 * The unread count is a shared piece of state (header badge, dashboard
 * panel, this page) so we use the StaffNotificationsContext for it.
 * When the user marks a single notification (or all) read we:
 *   1. Update local `notifications[].is_read` immediately.
 *   2. Decrement (or reset) the shared unread count immediately.
 *   3. POST to the server.
 *   4. On failure, roll back BOTH the local state and the shared count
 *      to their pre-click values, and surface an error toast.
 *
 * The context provides safe no-op fallbacks when rendered outside a
 * provider (legacy `staff_portal_v2_enabled=false` path), so this file
 * works in both v1 and v2 shells without a flag branch.
 */
export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { unreadCount, setUnreadCount, decrement, reset } = useStaffNotifications();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/org/notifications/my');
      const items = res.data.data || [];
      setNotifications(items);
      // Server-reported count wins on load.
      const serverUnread = typeof res.data.unread_count === 'number'
        ? res.data.unread_count
        : items.filter((n) => !n.is_read).length;
      setUnreadCount(serverUnread);
    } catch (e) { toast.error('Failed to load notifications'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const markRead = async (id) => {
    // Snapshot for rollback.
    const prevNotifications = notifications;
    const target = notifications.find((n) => n.id === id);
    if (!target || target.is_read) return;

    // Optimistic — flip the row + decrement shared badge.
    setNotifications((cur) => cur.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    decrement(1);

    try {
      await api.post(`/org/notifications/${id}/read`);
    } catch (e) {
      // Rollback both.
      setNotifications(prevNotifications);
      setUnreadCount(prevNotifications.filter((n) => !n.is_read).length);
      toast.error('Failed to mark as read');
    }
  };

  const markAllRead = async () => {
    const prevNotifications = notifications;
    const prevUnread = prevNotifications.filter((n) => !n.is_read).length;
    if (prevUnread === 0) return;

    // Optimistic — flip every row + zero the shared badge.
    setNotifications((cur) => cur.map((n) => ({ ...n, is_read: true })));
    reset();

    try {
      await api.post('/org/notifications/read-all');
      toast.success('All marked as read');
    } catch (e) {
      // Rollback both.
      setNotifications(prevNotifications);
      setUnreadCount(prevUnread);
      toast.error('Failed to mark all as read');
    }
  };

  // Polish-pass KPI band — additive only, computed client-side.
  const kpis = useMemo(() => {
    const read = notifications.filter(n => n.is_read).length;
    const critical = notifications.filter(n => /(critical|urgent|alert)/i.test(n.type || '') || /(critical|urgent)/i.test(n.priority || '')).length;
    return { total: notifications.length, unread: unreadCount, read, critical };
  }, [notifications, unreadCount]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Notifications</h2>
          <p className="text-muted-foreground mt-1" data-testid="notifications-unread-label">{unreadCount} unread</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read">
            <Check className="h-4 w-4 mr-2" /> Mark all read
          </Button>
        )}
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="notifications-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="notifications-summary-total">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total</span>
            <Inbox className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{kpis.total}</div>
        </div>
        <div className={`rounded-lg border p-3 ${kpis.unread > 0 ? 'border-sky-200 bg-sky-50/40' : 'border-slate-200 bg-white'}`} data-testid="notifications-summary-unread">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Unread</span>
            <Bell className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className={`mt-1 text-xl font-semibold tabular-nums ${kpis.unread > 0 ? 'text-sky-700' : 'text-slate-900'}`}>{kpis.unread}</div>
        </div>
        <div className="rounded-lg border bg-white p-3" data-testid="notifications-summary-read">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Read</span>
            <MailOpen className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{kpis.read}</div>
        </div>
        <div className={`rounded-lg border p-3 ${kpis.critical > 0 ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'}`} data-testid="notifications-summary-critical">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Critical</span>
            <AlertCircle className="h-3.5 w-3.5 text-slate-400" aria-hidden />
          </div>
          <div className={`mt-1 text-xl font-semibold tabular-nums ${kpis.critical > 0 ? 'text-red-700' : 'text-slate-900'}`}>{kpis.critical}</div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2" data-testid="notifications-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-md" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Bell}
              title="You're all caught up"
              description="No notifications right now. You'll see alerts, mentions, and approvals here."
              testid="notifications-empty"
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => (
            <Card key={n.id} className={`transition-colors ${!n.is_read ? 'border-primary/20 bg-accent/20' : ''}`} data-testid={`notification-card-${n.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className={`text-sm ${!n.is_read ? 'font-medium' : ''}`}>{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.message}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <StatusBadge status={n.type} />
                    <span className="text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                </div>
                {!n.is_read && (
                  <IconButton
                    label="Mark as read"
                    onClick={() => markRead(n.id)}
                    testid={`notification-mark-read-${n.id}`}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </IconButton>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
